// src/services/cookieConsentService.ts
import { logger } from '../utils/logger';
// GDPR-compliant cookie consent management

export interface CookiePreferences {
  necessary: boolean; // Always true, cannot be disabled
  analytics: boolean;
  performance: boolean;
  functional: boolean;
}

export interface ConsentData {
  preferences: CookiePreferences;
  timestamp: number;
  version: string; // Track consent version for GDPR compliance
}

const CONSENT_KEY = 'dice-cat-cookie-consent';
const CONSENT_VERSION = '1.0';

class CookieConsentService {
  private listeners: Set<(data: ConsentData | null) => void> = new Set();

  /**
   * Get current consent preferences
   */
  getConsent(): ConsentData | null {
    try {
      const stored = localStorage.getItem(CONSENT_KEY);
      if (!stored) return null;

      const data = JSON.parse(stored) as ConsentData;

      // If consent version changed, invalidate old consent
      if (data.version !== CONSENT_VERSION) {
        this.clearConsent();
        return null;
      }

      return data;
    } catch (error) {
      logger.error('Error reading cookie consent:', error);
      return null;
    }
  }

  /**
   * Save consent preferences
   */
  saveConsent(preferences: CookiePreferences): void {
    const data: ConsentData = {
      preferences: {
        ...preferences,
        necessary: true, // Always enforce necessary cookies
      },
      timestamp: Date.now(),
      version: CONSENT_VERSION,
    };

    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify(data));
      this.notifyListeners(data);
    } catch (error) {
      logger.error('Error saving cookie consent:', error);
    }
  }

  /**
   * Clear all consent data (user revokes consent)
   */
  clearConsent(): void {
    try {
      localStorage.removeItem(CONSENT_KEY);
      this.notifyListeners(null);
    } catch (error) {
      logger.error('Error clearing cookie consent:', error);
    }
  }

  /**
   * Check if user has given consent
   */
  hasConsent(): boolean {
    return this.getConsent() !== null;
  }

  /**
   * Check if specific cookie category is allowed
   */
  isAllowed(category: keyof CookiePreferences): boolean {
    const consent = this.getConsent();
    if (!consent) return false;
    return consent.preferences[category] === true;
  }

  /**
   * Accept all cookies
   */
  acceptAll(): void {
    this.saveConsent({
      necessary: true,
      analytics: true,
      performance: true,
      functional: true,
    });
  }

  /**
   * Accept only necessary cookies
   */
  acceptNecessaryOnly(): void {
    this.saveConsent({
      necessary: true,
      analytics: false,
      performance: false,
      functional: false,
    });
  }

  /**
   * Subscribe to consent changes
   */
  onChange(callback: (data: ConsentData | null) => void): () => void {
    this.listeners.add(callback);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Notify all listeners of consent changes
   */
  private notifyListeners(data: ConsentData | null): void {
    this.listeners.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        logger.error('Error in consent listener:', error);
      }
    });
  }

  /**
   * Get consent status for logging/debugging
   */
  getConsentStatus(): {
    hasConsent: boolean;
    necessary: boolean;
    analytics: boolean;
    performance: boolean;
    functional: boolean;
    timestamp: number | null;
  } {
    const consent = this.getConsent();
    if (!consent) {
      return {
        hasConsent: false,
        necessary: false,
        analytics: false,
        performance: false,
        functional: false,
        timestamp: null,
      };
    }

    return {
      hasConsent: true,
      ...consent.preferences,
      timestamp: consent.timestamp,
    };
  }
}

// Export singleton instance
export const cookieConsentService = new CookieConsentService();
